import axios from "axios";
import { getToken } from "@clerk/react";

/**
 * Shared Axios instance for all API calls.
 * Token injection uses Clerk's standalone getToken() — works outside React components.
 */
export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "http://localhost:8000/api",
  headers: { "Content-Type": "application/json" },
  timeout: 30_000,
});

api.interceptors.request.use(async (config) => {
  try {
    const token = await getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // token is null when user is not signed in -- proceed without header
    // (normal for pre-auth routes; protected endpoints will return 401
    // which Refine's onError handler routes to /login)
  } catch (error) {
    // getToken() threw -- this is an SDK/infrastructure error, NOT a
    // "user is unauthenticated" case. Reject the request to prevent sending
    // an unauthenticated request to a protected endpoint (which would trigger
    // a 401 -> onError -> redirect loop if the session is actually valid but
    // the SDK had a transient failure).
    console.error("[api] Clerk getToken() failed:", error);
    return Promise.reject(
      new axios.Cancel("Authentication token unavailable — Clerk SDK error"),
    );
  }
  return config;
});

/** Extract the server's error detail message, falling back to a default. */
export function getApiErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: string } | undefined)?.detail;
    return detail ?? fallback;
  }
  return fallback;
}
